import React from 'react';

export type TextCase = 'none' | 'uppercase' | 'lowercase' | 'titlecase';

// Drop shadows in REFERENCE px (canvas width 360), scaled by each surface's
// canvas scale like every other overlay dimension. They used to be fixed CSS
// px on every surface (2/4 px), which at the 1080 px publish canvas was a
// third of the card's shadow — "the shadow isn't picked up" (2026-10-07).
// The reference values keep a ~630 px card looking as it did.
export const TEXT_SHADOW_TITLE_REF = { y: 1.2, blur: 2.4, color: 'rgba(0, 0, 0, 0.8)' };
export const TEXT_SHADOW_SMALL_REF = { y: 0.6, blur: 1.8, color: 'rgba(0, 0, 0, 0.8)' };
/** The CSS text-shadow for a recipe at a canvas scale (overlay.html mirrors this). */
export function textShadowCss(variant: 'title' | 'small', scale: number): string {
  const r = variant === 'title' ? TEXT_SHADOW_TITLE_REF : TEXT_SHADOW_SMALL_REF;
  const px = (v: number) => `${Math.round(v * scale * 100) / 100}px`;
  return `0 ${px(r.y)} ${px(r.blur)} ${r.color}`;
}
/** The fit-mode backdrop blur, reference px (≈20 px on a ~630 px card). */
export const BACKDROP_BLUR_REF = 12;
export const backdropBlurCss = (scale: number) => `blur(${Math.round(BACKDROP_BLUR_REF * scale * 100) / 100}px)`;

export interface TextStyleProps {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  /** Drop shadow behind the text. Callers pass the hydrated value (config
   *  default is ON); omitting the prop leaves the surface shadow-free. */
  shadow?: boolean;
  /** Which shadow recipe: title gets the heavier one. */
  shadowVariant?: 'title' | 'small';
  textCase?: TextCase;
  /** Letter spacing as integer percent (0/undefined = default). */
  letterSpacing?: number;
  /** Unitless line-height multiplier; null/undefined = Auto (keep element default). */
  lineHeight?: number | null;
}

export interface ElementBoxProps {
  borderEnabled?: boolean;
  borderColor?: string;
  borderThickness?: number; // px at reference width 360
  borderRadius?: number;    // px at reference width 360
}

export function textCaseToCss(textCase?: TextCase): React.CSSProperties['textTransform'] {
  switch (textCase) {
    case 'uppercase': return 'uppercase';
    case 'lowercase': return 'lowercase';
    case 'titlecase': return 'capitalize';
    default: return undefined;
  }
}

export function buildTextStyle(p: TextStyleProps, scale: number = 1): React.CSSProperties {
  const decorations: string[] = [];
  if (p.underline) decorations.push('underline');
  if (p.strikethrough) decorations.push('line-through');
  return {
    fontWeight: p.bold ? 700 : undefined,
    fontStyle: p.italic ? 'italic' : undefined,
    textShadow: p.shadow ? textShadowCss(p.shadowVariant === 'title' ? 'title' : 'small', scale) : undefined,
    textDecoration: decorations.length ? decorations.join(' ') : undefined,
    textTransform: textCaseToCss(p.textCase),
    // em is font-relative, so it composes with every scaling pipeline
    // (preview overlayScale, Remotion resolutions, Playwright scale)
    // without extra multiplication.
    letterSpacing: p.letterSpacing ? `${p.letterSpacing / 100}em` : undefined,
    // Only emit when explicitly set — Auto (null/undefined) must keep the
    // element's existing leading-* class / default so legacy content
    // renders unchanged.
    lineHeight: p.lineHeight ?? undefined,
  };
}

export function buildBorderStyle(p: ElementBoxProps, scale: number = 1): React.CSSProperties {
  if (!p.borderEnabled || !p.borderThickness) {
    return {
      borderRadius: p.borderRadius != null ? Math.round(p.borderRadius * scale) : undefined,
    };
  }
  return {
    border: `${Math.max(1, Math.round((p.borderThickness || 0) * scale))}px solid ${p.borderColor || '#FFFFFF'}`,
    borderRadius: p.borderRadius != null ? Math.round(p.borderRadius * scale) : undefined,
  };
}
